import { memo, useCallback, useState } from "react";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from "@mui/material";

import ChipFilledOther from "../../../../common/components/chip/ChipFilledOther";
import ChipFilledPrimary from "../../../../common/components/chip/ChipFilledPrimary";
import ChipFilledWarning from "../../../../common/components/chip/ChipFilledWarning";
import EditIconButton from "../../../../common/components/icons/EditIconButton";
import RouterLink from "../../../../common/components/RouterLink";
import TableCellTruncated from "../../../../common/components/table/TableCellTruncated";
import {
  getSKUDisplayLabel,
  isLegacySKU,
  isSKUAssigned,
} from "../../../../common/variables/skuOptions";
import { IDeviceComponent, ISKUGroup } from "../../types/groupedDevices";
import DeleteDeviceContainer from "../DeleteDeviceContainer";
import DeviceConnectedModelsColumn from "../DeviceConnectedModelsColumn";

interface IGroupedDevicesTableBodyRowProps {
  group: ISKUGroup;
}

/**
 * Renders the SKU display value for the master row
 * @param {object} props - Component props
 * @param {string | null} props.skuNumber - The SKU number to display
 * @return {JSX.Element} The rendered SKU cell
 */
const SKUCell = ({ skuNumber }: { skuNumber: string | null }): JSX.Element => {
  const isAssigned = isSKUAssigned(skuNumber);
  const displayLabel = getSKUDisplayLabel(skuNumber);

  if (!isAssigned) {
    return <Typography variant="body2">-</Typography>;
  }

  if (isLegacySKU(skuNumber)) {
    return <Typography variant="body2">Legacy</Typography>;
  }

  return (
    <TableCellTruncated
      tooltipText={displayLabel}
      text={displayLabel}
      TypographyProps={{ variant: "body2" }}
    />
  );
};

/**
 * Renders a single device row in the detail table
 * @param {object} props - Component props
 * @param {IDeviceComponent} props.device - The device to render
 * @return {JSX.Element} The rendered device row
 */
const DeviceDetailRow = ({
  device,
}: {
  device: IDeviceComponent;
}): JSX.Element => {
  const nodeName = device.node.name ?? device.node.id.split("#N#").at(-1) ?? "";

  return (
    <TableRow
      sx={{
        "&:last-child td, &:last-child th": { borderBottom: 0 },
      }}
    >
      {/* Indent column to align with master row (48px - 16px container padding) */}
      <TableCell sx={{ width: 32 }} />
      {/* Device Name */}
      <TableCell>
        <Box sx={{ overflowX: "clip", whiteSpace: "nowrap" }}>
          <Tooltip title={device.deviceName}>
            <span style={{ cursor: "pointer" }}>
              <RouterLink
                to={`/device/${encodeURIComponent(device.rowId)}`}
                title={device.deviceName}
                state={{ deviceDatum: device.originalRow }}
              />
            </span>
          </Tooltip>
        </Box>
      </TableCell>
      {/* Node */}
      <TableCell>
        <Box sx={{ overflowX: "clip", whiteSpace: "nowrap" }}>
          <Tooltip title={nodeName}>
            <Typography component="span" variant="body2">
              {nodeName}
            </Typography>
          </Tooltip>
        </Box>
      </TableCell>
      {/* Camera IP */}
      <TableCell>
        <Box sx={{ overflowX: "clip", whiteSpace: "nowrap" }}>
          <Tooltip title={device.cameraIpAddress}>
            <Typography component="span" variant="body2">
              {device.cameraIpAddress || "-"}
            </Typography>
          </Tooltip>
        </Box>
      </TableCell>
      {/* Models - wraps at 2 chips per row, determines row height */}
      <TableCell>
        <Box
          onClick={e => e.stopPropagation()}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "0.5em",
          }}
        >
          <DeviceConnectedModelsColumn device={device.originalRow} />
        </Box>
      </TableCell>
      {/* Component Serial Number - horizontal scroll on overflow */}
      <TableCell>
        <Box sx={{ overflowX: "clip", whiteSpace: "nowrap" }}>
          <Tooltip title={device.componentSerialNumber}>
            <Typography component="span" variant="body2">
              {device.componentSerialNumber || "-"}
            </Typography>
          </Tooltip>
        </Box>
      </TableCell>
      {/* Spacer - stretchable, pushes Status/Actions to the right */}
      <TableCell />
      {/* Status */}
      <TableCell>
        {device.isOnline ? (
          <ChipFilledPrimary label="Online" />
        ) : (
          <ChipFilledOther label="Offline" />
        )}
      </TableCell>
      {/* Actions */}
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
          <RouterLink
            to={`/device/edit/${encodeURIComponent(device.rowId)}`}
            state={{
              deviceDatum: device.originalRow,
              useBackToDevicesPage: true,
            }}
          >
            <EditIconButton />
          </RouterLink>
          <DeleteDeviceContainer row={device.originalRow} />
        </Box>
      </TableCell>
    </TableRow>
  );
};

/**
 * Master-detail row component following DeploymentHealthTableBodyRow pattern.
 * - Master row: SKU summary with expand/collapse
 * - Detail row: Nested table with individual devices
 * @param {IGroupedDevicesTableBodyRowProps} props - Component props
 * @return {JSX.Element} The rendered row
 */
const GroupedDevicesTableBodyRowInner = ({
  group,
}: IGroupedDevicesTableBodyRowProps): JSX.Element => {
  const [open, setOpen] = useState(false);

  const handleToggleOpen = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  return (
    <>
      {/* Master Row */}
      <TableRow
        sx={{
          backgroundColor: (theme: Theme) => theme.palette.otherElevation.main,
          "& > td": {
            borderBottomColor: (theme: Theme) =>
              open
                ? theme.palette.otherOutlineBorder.main
                : theme.palette.otherDivider.main,
          },
          cursor: "pointer",
          "&:hover": {
            backgroundColor: (theme: Theme) =>
              theme.palette.otherBackground.main,
          },
        }}
        onClick={handleToggleOpen}
      >
        {/* Expand/Collapse Icon */}
        <TableCell sx={{ width: 48 }}>
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation();

              handleToggleOpen();
            }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        {/* SKU */}
        <TableCell sx={{ width: 150 }}>
          <SKUCell skuNumber={group.skuNumber} />
        </TableCell>
        {/* SKU Serial Number */}
        <TableCell sx={{ width: 300, overflowX: "clip", whiteSpace: "nowrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TableCellTruncated
              tooltipText={group.serialNumber || ""}
              text={group.serialNumber || "-"}
              TypographyProps={{ variant: "body2", component: "span" }}
            />
            {group.parentProductSerialNumber && (
              <Tooltip
                title={`Parent system: ${group.parentProductSerialNumber}`}
              >
                <Chip
                  label={`Parent: ${group.parentProductSerialNumber}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.7rem", height: 22 }}
                />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        {/* Configuration */}
        <TableCell sx={{ width: 362 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TableCellTruncated
              tooltipText={group.configurationName || ""}
              text={
                (group.configurationName || "-") + ` (${group.devices.length})`
              }
              TypographyProps={{ variant: "body2", component: "span" }}
            />
          </Box>
        </TableCell>
        {/* Spacer - stretchable, pushes Status/Actions to the right */}
        <TableCell />
        {/* Aggregate Status */}
        <TableCell sx={{ width: 115 }}>
          {group.aggregateStatus === "online" && (
            <ChipFilledPrimary label="Online" />
          )}
          {group.aggregateStatus === "degraded" && (
            <ChipFilledWarning
              label="Degraded"
              sx={{
                "&.MuiChip-root": {
                  fontSize: "0.75rem",
                  padding: "4px 8px",
                },
              }}
            />
          )}
          {group.aggregateStatus === "offline" && (
            <ChipFilledOther label="Offline" />
          )}
        </TableCell>
        {/* Actions - empty for master row */}
        <TableCell sx={{ width: 100 }} />
      </TableRow>

      {/* Detail Row - Nested Device Table (only rendered when open) */}
      {open && (
        <TableRow>
          <TableCell
            colSpan={7}
            sx={{
              py: 0,
              pl: 2,
              pr: 0,
              borderBottom: (theme: Theme) =>
                `1px solid ${theme.palette.otherDivider.main}`,
              backgroundColor: (theme: Theme) =>
                theme.palette.otherBackground.main,
            }}
          >
            <Box sx={{ py: 2 }}>
              <Box sx={{ overflowX: "auto", width: "100%" }}>
                <Table
                  size="small"
                  sx={{ tableLayout: "fixed", minWidth: 1129, width: "100%" }}
                >
                  <TableHead>
                    <TableRow>
                      {/* Indent column to align with master row (48px - 16px container padding) */}
                      <TableCell sx={{ width: 32 }} />
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          fontSize: "12px",
                          width: 216,
                        }}
                      >
                        DEVICE NAME
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "12px", width: 156 }}
                      >
                        NODE
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "12px", width: 130 }}
                      >
                        CAMERA IP
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "12px", width: 300 }}
                      >
                        MODELS
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "12px", width: 80 }}
                      >
                        <Tooltip title="Component Serial Number">
                          <span>CSN</span>
                        </Tooltip>
                      </TableCell>
                      {/* Spacer - stretchable, pushes Status/Actions to the right */}
                      <TableCell />
                      {/* Status */}
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "12px", width: 115 }}
                      >
                        STATUS
                      </TableCell>
                      {/* Actions */}
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: "12px", width: 100 }}
                      >
                        ACTIONS
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.devices.map(device => (
                      <DeviceDetailRow key={device.rowId} device={device} />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

/**
 * Custom comparison to prevent re-renders when group data hasn't actually changed
 * @param {IGroupedDevicesTableBodyRowProps} prevProps - Previous props
 * @param {IGroupedDevicesTableBodyRowProps} nextProps - Next props
 * @return {boolean} True if props are equal
 */
const areGroupPropsEqual = (
  prevProps: IGroupedDevicesTableBodyRowProps,
  nextProps: IGroupedDevicesTableBodyRowProps
): boolean => {
  const prev = prevProps.group;
  const next = nextProps.group;

  // Quick reference check
  if (prev === next) {
    return true;
  }

  // Compare primitive fields
  if (
    prev.groupId !== next.groupId ||
    prev.skuNumber !== next.skuNumber ||
    prev.serialNumber !== next.serialNumber ||
    prev.configurationName !== next.configurationName ||
    prev.aggregateStatus !== next.aggregateStatus ||
    prev.isLegacy !== next.isLegacy ||
    prev.parentProductSerialNumber !== next.parentProductSerialNumber
  ) {
    return false;
  }

  // Compare array length
  if (prev.devices.length !== next.devices.length) {
    return false;
  }

  // Compare devices by key fields that affect rendering
  for (let i = 0; i < prev.devices.length; i++) {
    const pDevice = prev.devices[i];
    const nDevice = next.devices[i];

    if (
      pDevice.rowId !== nDevice.rowId ||
      pDevice.deviceName !== nDevice.deviceName ||
      pDevice.isOnline !== nDevice.isOnline ||
      pDevice.cameraIpAddress !== nDevice.cameraIpAddress ||
      pDevice.componentSerialNumber !== nDevice.componentSerialNumber ||
      pDevice.node.id !== nDevice.node.id ||
      pDevice.models.length !== nDevice.models.length
    ) {
      return false;
    }

    // Check if any model's isRunning status changed (affects chip display)
    for (let j = 0; j < pDevice.models.length; j++) {
      if (pDevice.models[j].isRunning !== nDevice.models[j]?.isRunning) {
        return false;
      }
    }
  }

  return true;
};

const GroupedDevicesTableBodyRow = memo(
  GroupedDevicesTableBodyRowInner,
  areGroupPropsEqual
);

GroupedDevicesTableBodyRow.displayName = "GroupedDevicesTableBodyRow";

export default GroupedDevicesTableBodyRow;
